import { msg } from '@lingui/macro'
import { ApplicationTypes, attachComponent } from 'jms-domain'

import {
  customPriceFooterComponent,
  customSceneEntityComponent,
  customSummaryPageComponent,
  finalizeButtonComponent,
  finalizePageComponent,
  memoEval,
  propertyPageComponent,
  sidebarTabsComponent,
} from '@salsita-ai/platform-project-sdk/domain'

import { CustomPriceFooter } from '../ui/CustomPriceFooter'
import { FinalizePage } from '../ui/FinalizePage'
import { Recipes } from '../ui/Recipes'
import { RoomContent } from '../ui/RoomContent'
import { RoomLayout } from '../ui/RoomLayout'
import { Summary } from '../ui/Summary'
import { RoomObject } from './RoomObject'

export const roomComponents = [
  attachComponent
    .withComponent(sidebarTabsComponent, 'default')
    .withEntity('Room')
    .attach(
      memoEval(
        () => [],
        () => ({
          tabs: [
            {
              label: msg({ id: 'navigation.room', message: 'Design layout' }),
              navigationStates: [ApplicationTypes.NavigationState.Room],
            },
            {
              label: msg({ id: 'navigation.roomContent', message: 'Edit cabinets' }),
              navigationStates: [ApplicationTypes.NavigationState.RoomContent],
            },
          ],
        })
      )
    ),

  attachComponent
    .withComponent(customSceneEntityComponent, 'room')
    .withEntity('Room')
    .attach(
      memoEval(
        self => [self.id, ...self.$walls.map(w => w.id)],
        (id, ...wallIds) => ({
          node: <RoomObject id={id} wallIds={wallIds} />,
        })
      )
    ),

  attachComponent
    .withComponent(propertyPageComponent, 'roomLayout')
    .withEntity('Room')
    .withFilter(s => ({
      navigationStates: [ApplicationTypes.NavigationState.Room],
      selectedEntityIds: [s.id, ...s.$walls.map(w => w.id)],
    }))
    .attach(
      memoEval(
        s => [s.id, ...s.$walls.map(w => w.id)],
        (id, ...wallIds) => ({
          page: <RoomLayout id={id} wallIds={wallIds} />,
        })
      )
    ),

  attachComponent
    .withComponent(propertyPageComponent, 'roomContent')
    .withEntity('Room')
    .withFilter(s => ({
      navigationStates: [ApplicationTypes.NavigationState.RoomContent],
      selectedEntityIds: [s.id],
    }))
    .attach(
      memoEval(
        s => [s.id, s.cabinetColor, s.powderCoatType],
        (id, cabinetColor, powderCoatType) => ({
          page: <RoomContent id={id} cabinetColor={cabinetColor} powderCoatType={powderCoatType} />,
        })
      )
    ),

  attachComponent
    .withComponent(propertyPageComponent, 'recipes')
    .withEntity('Room')
    .withFilter(s => ({
      navigationStates: [ApplicationTypes.NavigationState.Recipes],
      selectedEntityIds: [s.id],
    }))
    .attach(
      memoEval(
        s => [s.id],
        id => ({
          page: <Recipes id={id} />,
        })
      )
    ),

  attachComponent
    .withComponent(customSummaryPageComponent, 'summary')
    .withEntity('Room')
    .withFilter(s => s.$items.length > 0)
    .attach(
      memoEval(
        s => [s.$summary],
        summary => ({
          summaryNavigationState: ApplicationTypes.NavigationState.Summary,
          page: <Summary summary={summary} />,
        })
      )
    ),

  attachComponent
    .withComponent(finalizePageComponent, 'default')
    .withEntity('Room')
    .withFilter(s => s.$items.length > 0)
    .attach(
      memoEval(
        () => [],
        () => ({
          finalizeNavigationStates: [
            ApplicationTypes.NavigationState.Finalize,
            ApplicationTypes.NavigationState.FinalizeGlamour,
          ],
          page: <FinalizePage backNavigationState={ApplicationTypes.NavigationState.Summary} />,
        })
      )
    ),

  // Hide summary button when there are no items in the room.½
  attachComponent
    .withComponent(finalizeButtonComponent, 'default')
    .withEntity('Room')
    .withFilter(s => s.$items.length === 0)
    .attach(
      memoEval(
        () => [],
        () => ({
          button: null,
        })
      )
    ),

  attachComponent
    .withEntity('Room')
    .withComponent(customPriceFooterComponent, 'default')
    .attach(
      memoEval(
        s => [s.$appliancePrice, s.$cabinetsAndPartsPrice, s.$items.length],
        (appliancePrice, cabinetsAndPartsPrice, itemCount) => ({
          node: (
            <CustomPriceFooter
              appliancePrice={appliancePrice}
              cabinetsAndPartsPrice={cabinetsAndPartsPrice}
              itemCount={itemCount}
            />
          ),
        })
      )
    ),
]
